import type { CandleWithFlag } from "features/chart";

export interface MACDResult {
  macd: (number | null)[];
  signal: (number | null)[];
  histogram: (number | null)[];
}

function calcEMA(values: (number | null)[], period: number): (number | null)[] {
  const result: (number | null)[] = new Array(values.length).fill(null);
  const k = 2 / (period + 1);
  let prev: number | null = null;
  let seedSum = 0, seedCount = 0;

  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null) continue;
    if (prev === null) {
      seedSum += v;
      seedCount++;
      if (seedCount === period) {
        prev = seedSum / period;
        result[i] = prev;
      }
    } else {
      prev = v * k + prev * (1 - k);
      result[i] = prev;
    }
  }
  return result;
}

export function calcMACD(
  data: CandleWithFlag[],
  fastPeriod: number,
  slowPeriod: number,
  signalPeriod: number,
): MACDResult {
  const n = data.length;
  const closes: (number | null)[] = data.map(c => ("close" in c ? c.close : null));

  const fastEMA = calcEMA(closes, fastPeriod);
  const slowEMA = calcEMA(closes, slowPeriod);

  const macdLine: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const f = fastEMA[i], s = slowEMA[i];
    if (f !== null && s !== null) macdLine[i] = f - s;
  }

  const signalLine = calcEMA(macdLine, signalPeriod);

  const histogram: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const m = macdLine[i], s = signalLine[i];
    if (m !== null && s !== null) histogram[i] = m - s;
  }

  return { macd: macdLine, signal: signalLine, histogram };
}
